import { getApiUrl } from "../config/env";
import { useSyncExternalStore } from "react";
import {
  CARE_SERVICE_GROUPS,
  COMMUNITY_SERVICE_DEFINITIONS,
  SERVICES_OPTIONS,
  SPECIALIZATIONS_OPTIONS,
  TAXONOMY_VERSION,
  SERVICE_LABEL_TO_CANONICAL,
  SERVICE_CANONICAL_TO_LABEL,
} from "../generated/careTaxonomy";
import { applyCapabilityVariantsFromTaxonomy } from "./capabilityUtils";

type TaxonomySyncResponse = {
  status: "success";
  version: string;
  service_label_to_canonical?: Record<string, string>;
  service_canonical_to_label?: Record<string, string>;
  capability_variants?: Record<string, string[]>;
  alias_to_canonical?: Record<string, string>;
  service_options?: unknown;
  specialization_options?: unknown;
  community_services?: unknown;
  discovery_groups?: unknown;
};

export type CareTaxonomyOptions = {
  services: readonly string[];
  specializations: readonly string[];
};

export type CommunityServiceCatalogEntry = {
  key: string;
  label: string;
  aliases: readonly string[];
  group: string;
  caseSelectable: boolean;
  coverageMode: string;
  regulated: boolean;
  inSearchFilters: boolean;
};

export type CareServiceCatalogGroup = {
  key: string;
  title: string;
};

export type CareTaxonomyCatalog = CareTaxonomyOptions & {
  communityServices: readonly CommunityServiceCatalogEntry[];
  discoveryGroups: readonly CareServiceCatalogGroup[];
};

const GENERATED_OPTIONS: CareTaxonomyOptions = {
  services: SERVICES_OPTIONS,
  specializations: SPECIALIZATIONS_OPTIONS,
};

const GENERATED_CATALOG: CareTaxonomyCatalog = {
  ...GENERATED_OPTIONS,
  communityServices: COMMUNITY_SERVICE_DEFINITIONS,
  discoveryGroups: CARE_SERVICE_GROUPS,
};

const BASE_REVERSE_MAPPINGS: Record<string, string> = {
  ...SERVICE_LABEL_TO_CANONICAL,
};
const BASE_DISPLAY_MAPPINGS: Record<string, string> = {
  ...SERVICE_CANONICAL_TO_LABEL,
};

let ACTIVE_CATALOG_VERSION: string = TAXONOMY_VERSION;
let activeCatalogEtag: string | null = null;
let inFlightSync: Promise<void> | null = null;
let lastSyncAtMs = 0;
const SYNC_TTL_MS = 5 * 60 * 1000;

let activeReverseMappings: Record<string, string> = {
  ...BASE_REVERSE_MAPPINGS,
};
let activeDisplayMappings: Record<string, string> = {
  ...BASE_DISPLAY_MAPPINGS,
};
let activeCatalog: CareTaxonomyCatalog = GENERATED_CATALOG;
let activeOptions: CareTaxonomyOptions = GENERATED_OPTIONS;
const optionListeners = new Set<() => void>();

function notifyOptionListeners(): void {
  optionListeners.forEach((listener) => listener());
}

function selectableLabels(
  entries: readonly { label: string }[],
  fallback: readonly string[]
): readonly string[] {
  const labels = Array.from(
    new Set(
      entries
        .map((entry) => entry?.label?.trim())
        .filter((label): label is string => Boolean(label))
    )
  );
  return labels.length > 0 ? [...labels, "Other"] : fallback;
}

function isSelectableOptionArray(value: unknown): value is Array<{ label: string }> {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        typeof entry === "object" &&
        entry !== null &&
        typeof (entry as { label?: unknown }).label === "string" &&
        Boolean((entry as { label: string }).label.trim())
    )
  );
}

function normalizeCommunityServiceEntry(
  value: unknown
): CommunityServiceCatalogEntry | null {
  if (typeof value !== "object" || value === null) return null;
  const entry = value as Record<string, unknown>;
  const caseSelectable = entry.case_selectable ?? entry.caseSelectable;
  const coverageMode = entry.coverage_mode ?? entry.coverageMode;
  const inSearchFilters = entry.in_search_filters ?? entry.inSearchFilters;
  if (
    typeof entry.key !== "string" ||
    !entry.key.trim() ||
    typeof entry.label !== "string" ||
    !entry.label.trim() ||
    !Array.isArray(entry.aliases) ||
    !entry.aliases.every((alias) => typeof alias === "string") ||
    typeof entry.group !== "string" ||
    !entry.group.trim() ||
    typeof caseSelectable !== "boolean" ||
    typeof coverageMode !== "string" ||
    !coverageMode.trim() ||
    typeof entry.regulated !== "boolean" ||
    typeof inSearchFilters !== "boolean"
  ) {
    return null;
  }
  return {
    key: entry.key,
    label: entry.label,
    aliases: entry.aliases,
    group: entry.group,
    caseSelectable,
    coverageMode,
    regulated: entry.regulated,
    inSearchFilters,
  };
}

function isCareServiceGroup(value: unknown): value is CareServiceCatalogGroup {
  if (typeof value !== "object" || value === null) return false;
  const group = value as Record<string, unknown>;
  return (
    typeof group.key === "string" &&
    Boolean(group.key.trim()) &&
    typeof group.title === "string" &&
    Boolean(group.title.trim())
  );
}

function toValidatedCatalog(
  data: TaxonomySyncResponse
): CareTaxonomyCatalog | null {
  if (
    !isSelectableOptionArray(data.service_options) ||
    !isSelectableOptionArray(data.specialization_options) ||
    !Array.isArray(data.community_services) ||
    !Array.isArray(data.discovery_groups) ||
    !data.discovery_groups.every(isCareServiceGroup)
  ) {
    return null;
  }

  const communityServices = data.community_services.map(
    normalizeCommunityServiceEntry
  );
  if (communityServices.some((service) => service === null)) return null;
  const normalizedCommunityServices = communityServices as CommunityServiceCatalogEntry[];

  const knownGroups = new Set(data.discovery_groups.map((group) => group.key));
  if (
    normalizedCommunityServices.some(
      (service) => !knownGroups.has(service.group)
    )
  ) {
    return null;
  }

  return {
    services: selectableLabels(data.service_options, GENERATED_OPTIONS.services),
    specializations: selectableLabels(
      data.specialization_options,
      GENERATED_OPTIONS.specializations
    ),
    communityServices: normalizedCommunityServices,
    discoveryGroups: data.discovery_groups,
  };
}

function refreshActiveDisplayMapFromReverse(): void {
  const next: Record<string, string> = { ...BASE_DISPLAY_MAPPINGS };
  for (const [alias, canonical] of Object.entries(activeReverseMappings)) {
    if (!next[canonical]) {
      next[canonical] = alias;
    }
  }
  activeDisplayMappings = next;
}

function mergeServiceReverseMappings(
  serviceLabelToCanonical?: Record<string, string>,
  capabilityAliasToCanonical?: Record<string, string>
): Record<string, string> {
  // Capability aliases broaden search; service_options labels win for display and save.
  return {
    ...(capabilityAliasToCanonical || {}),
    ...BASE_REVERSE_MAPPINGS,
    ...(serviceLabelToCanonical || {}),
  };
}

export function getActiveServiceCatalogVersion(): string {
  return ACTIVE_CATALOG_VERSION;
}

export function getActiveReverseServiceMappings(): Record<string, string> {
  return activeReverseMappings;
}

export function getActiveServiceNameMappings(): Record<string, string> {
  return activeDisplayMappings;
}

export function getActiveCareTaxonomyOptions(): CareTaxonomyOptions {
  return activeOptions;
}

export function getActiveCareTaxonomyCatalog(): CareTaxonomyCatalog {
  return activeCatalog;
}

export function useCareTaxonomyOptions(): CareTaxonomyOptions {
  return useSyncExternalStore(
    (listener) => {
      optionListeners.add(listener);
      return () => optionListeners.delete(listener);
    },
    getActiveCareTaxonomyOptions,
    () => GENERATED_OPTIONS
  );
}

export function useCareTaxonomyCatalog(): CareTaxonomyCatalog {
  return useSyncExternalStore(
    (listener) => {
      optionListeners.add(listener);
      return () => optionListeners.delete(listener);
    },
    getActiveCareTaxonomyCatalog,
    () => GENERATED_CATALOG
  );
}

/** Reset runtime maps to generated taxonomy (tests). */
export function resetActiveTaxonomyMappingsForTests(): void {
  activeReverseMappings = { ...BASE_REVERSE_MAPPINGS };
  activeDisplayMappings = { ...BASE_DISPLAY_MAPPINGS };
  activeCatalog = GENERATED_CATALOG;
  activeOptions = GENERATED_OPTIONS;
  ACTIVE_CATALOG_VERSION = TAXONOMY_VERSION;
  activeCatalogEtag = null;
  lastSyncAtMs = 0;
  notifyOptionListeners();
}

/** @deprecated Capability aliases are included in taxonomy sync; kept for tests. */
export function applyBackendServiceCatalog(catalog: {
  alias_to_canonical?: Record<string, string>;
  version?: string;
}): void {
  if (catalog.alias_to_canonical) {
    activeReverseMappings = mergeServiceReverseMappings(
      undefined,
      catalog.alias_to_canonical
    );
    refreshActiveDisplayMapFromReverse();
  }
  if (catalog.version) {
    ACTIVE_CATALOG_VERSION = catalog.version;
  }
}

function applyTaxonomyPayload(data: TaxonomySyncResponse): boolean {
  const nextCatalog = toValidatedCatalog(data);
  if (!nextCatalog) return false;

  activeReverseMappings = mergeServiceReverseMappings(
    data.service_label_to_canonical,
    data.alias_to_canonical
  );

  if (data.service_canonical_to_label) {
    activeDisplayMappings = {
      ...BASE_DISPLAY_MAPPINGS,
      ...data.service_canonical_to_label,
    };
  } else {
    refreshActiveDisplayMapFromReverse();
  }

  if (data.capability_variants) {
    applyCapabilityVariantsFromTaxonomy(data.capability_variants);
  }
  activeCatalog = nextCatalog;
  activeOptions = {
    services: nextCatalog.services,
    specializations: nextCatalog.specializations,
  };
  ACTIVE_CATALOG_VERSION = data.version || TAXONOMY_VERSION;
  notifyOptionListeners();
  return true;
}

export async function syncCareTaxonomyFromBackend(
  options?: { force?: boolean }
): Promise<void> {
  const now = Date.now();
  if (!options?.force && now - lastSyncAtMs < SYNC_TTL_MS) {
    return;
  }
  if (inFlightSync) {
    return inFlightSync;
  }

  inFlightSync = (async () => {
    try {
      const res = await fetch(`${getApiUrl()}/capabilities/taxonomy`, {
        method: "GET",
        credentials: "include",
        cache: "no-cache",
        headers: activeCatalogEtag
          ? { "If-None-Match": activeCatalogEtag }
          : undefined,
      });

      if (res.status === 304) {
        lastSyncAtMs = Date.now();
        return;
      }

      if (res.ok) {
        const data = (await res.json()) as TaxonomySyncResponse;
        if (data?.status === "success") {
          if (applyTaxonomyPayload(data)) {
            activeCatalogEtag = res.headers?.get("ETag") ?? null;
            lastSyncAtMs = Date.now();
          }
        }
      }
    } catch {
      // Keep generated fallback when backend taxonomy is unavailable.
    } finally {
      inFlightSync = null;
    }
  })();

  return inFlightSync;
}

/** @deprecated Use syncCareTaxonomyFromBackend */
export const syncServiceMappingsFromBackend = syncCareTaxonomyFromBackend;
